'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

const STICKY_OFFSET = 32;

export default function BookingFormScroller({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef({ start: 0, end: 0 });
  const safariRef = useRef(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setEnabled(media.matches);
    update();
    safariRef.current = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    if (!enabled) {
      card.style.transform = 'none';
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    let frame = 0;
    let measureFrame = 0;
    const measure = () => {
      const root = rootRef.current;
      const card = cardRef.current;
      if (!root || !card) return;
      const container = root.parentElement;
      if (!container) return;

      const cardTop = root.getBoundingClientRect().top + window.scrollY;
      const containerTop = container.getBoundingClientRect().top + window.scrollY;
      const containerHeight = container.offsetHeight;
      const cardHeight = card.offsetHeight;
      const start = cardTop - STICKY_OFFSET;
      const end = containerTop + containerHeight - cardHeight - STICKY_OFFSET;
      layoutRef.current = { start, end };
    };

    const update = () => {
      const card = cardRef.current;
      if (!card) return;
      const { start, end } = layoutRef.current;
      if (end <= start) {
        card.style.transform = 'translateY(0px)';
        return;
      }
      const scrollY = window.scrollY;
      const offset = Math.max(0, Math.min(scrollY - start, end - start));
      const roundedOffset = Math.round(offset);
      card.style.transform = safariRef.current
        ? `translateY(${roundedOffset}px)`
        : `translate3d(0, ${roundedOffset}px, 0)`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    const onMeasure = () => {
      if (measureFrame) return;
      measureFrame = window.requestAnimationFrame(() => {
        measureFrame = 0;
        measure();
        update();
      });
    };

    measure();
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onMeasure);
    const ro = new ResizeObserver(onMeasure);
    if (rootRef.current) ro.observe(rootRef.current);
    if (rootRef.current?.parentElement) ro.observe(rootRef.current.parentElement);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onMeasure);
      ro.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      if (measureFrame) window.cancelAnimationFrame(measureFrame);
    };
  }, [enabled]);

  return (
    <div ref={rootRef} className="w-full lg:relative">
      <div
        ref={cardRef}
        className="w-full bg-card text-card-foreground rounded-xl shadow-lg p-6 border border-border lg:will-change-transform"
      >
        {children}
      </div>
    </div>
  );
}
